import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ChevronDown,
  Info,
  Loader2,
  MessageCircle,
  Paperclip,
  ShieldAlert,
  Upload,
  X,
} from "lucide-react";
import AdminEmptyState from "@/components/AdminEmptyState";
import Modal from "@/components/Modal";
import { GetRedeemedSponsorsAPI } from "@/services/api/sponsor/getRedeemedSponsors";
import { compensationUploadAPI } from "@/services/api/sponsor/compensationApi";
import {
  reviewEnsureFollowersStatusAPI,
  reviewEnsureFollowersTaskAPI,
  reviewRegisterAppTaskAPI,
  reviewUploadProofAPI,
} from "@/services/api/sponsor/reviewTaskApi";
import {
  StoreRegisteredUsernamesProofAPI,
  StoreSponsorTaskProofAPI,
} from "@/services/api/sponsor/storeSponsorTaskProof";
import { cloudinaryUpload, cloudinaryUploadMedia } from "@/services/middleware/uploadImage";
import { openChatRoom } from "@/services/middleware/chatHelper";
import {
  anyTaskFailedMaxReview,
  calculateCompensation,
  canAttemptActivity,
  getActivityHasPreDeadlineProof,
  getEffectiveJobStatus as resolveEffectiveJobStatus,
  isTaskCompleted,
  isTaskFinallyFailed,
  getSponsorProofIgAccounts,
  getSponsorProofMedia,
  getSponsorProofRegisteredUsernames,
} from "@/utils/sponsorStatus";
import {
  getLastCureDay,
  isOnOrAfterEndOfCurePeriod,
  parseLocalDate,
} from "@/utils/deadline";

type ProofMedia = { type: string; url: string };

/**
 * The requirement the API resolved for a task, from `requirement` on the task
 * object. Resolution is server-side because it needs the task row AND the
 * activity row; clients must branch on these fields and never on the raw
 * `requirements` JSON or the title.
 */
type ResolvedRequirement = {
  followerCount?: number | null;
  followerCountCap?: number | null;
  followTargets?: string[] | null;
  usernameMin?: number | null;
  media?: { min?: number | null; max?: number | null; mime?: string[] | null } | null;
  accountsToRegister?: string[] | null;
};

type RedeemedSponsorTask = {
  id: string;
  title?: string | null;
  description?: string | null;
  type?: string | null;
  completed?: boolean | null;
  sponsor_task_completion_id?: string | null;
  proof?: any;
  requirements?: any;
  requirement?: ResolvedRequirement;
  queue_job_status?: string | null;
  queue_job_id?: string | null;
  review_count?: number | null;
  utime?: string | null;
};
const ACTIONABLE_TASK_TYPES = [
  "review",
  "follow_and_like_post",
  "ensure_followers",
  "upload",
  "register_app",
  "publish_story_post",
];

const CURE_PERIOD_MS = 7 * 24 * 60 * 60 * 1000;

const formatMMDDYYYY = (date: Date) =>
  `${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}-${date.getFullYear()}`;

/** Deadline end (23:59:59.999) and cure end (deadline end + 7 days). */
const getDeadlineAndCureEnd = (deadline?: string | null) => {
  const deadlineDate = deadline ? parseLocalDate(deadline) : null;
  if (!deadlineDate) return null;
  const deadlineEnd = new Date(deadlineDate.getTime());
  deadlineEnd.setHours(23, 59, 59, 999);
  return {
    deadlineEnd,
    cureEnd: new Date(deadlineEnd.getTime() + CURE_PERIOD_MS),
  };
};

/**
 * Media entries from any of the proof shapes the backend stores.
 *
 * Delegates to the shared helper in @/utils/sponsorStatus so this page, the
 * sponsor check pages and the RN screens all agree on what a proof contains.
 */
const getProofMedia = (proof: any): ProofMedia[] =>
  getSponsorProofMedia(proof)
    .filter((item: any) => item && item.url)
    .map((item: any) => ({ type: item.type || "image", url: item.url }));

/** IG handles a student submitted for an `ensure_followers` task. */
const getIgAccountsProof = (proof: any): string[] =>
  getSponsorProofIgAccounts(proof);

/** Usernames a student submitted for a `register_app` task. */
const getRegisteredUsernamesProof = (proof: any): string[] =>
  getSponsorProofRegisteredUsernames(proof);

const INSTAGRAM_POST_URL_PATTERN =
  /^https:\/\/(?:www\.)?instagram\.com\/(?:p|reel|reels|tv|stories|share)\/[^/?#\s]+/i;

const isInstagramPostUrl = (value: string): boolean =>
  INSTAGRAM_POST_URL_PATTERN.test(value.trim());

const describeEvidenceConflicts = (conflicts: any[]): string =>
  (Array.isArray(conflicts) ? conflicts : [])
    .map((conflict) => {
      const handle =
        conflict?.username || conflict?.handle || conflict?.evidence_key || "";
      const sponsor = conflict?.sponsor_name;
      return sponsor ? `@${handle} (${sponsor})` : `@${handle}`;
    })
    .filter((value) => value !== "@")
    .join(", ");

/**
 * The IG handles a student must follow, for an `ensure_followers` task.
 *
 * Read from the RESOLVED `requirement` the API attaches to the task. It used to
 * be parsed out of the task title, which coupled the task to every OTHER task in
 * the same activity: `parseTargetIgAccounts` searched all tasks and returned the
 * first title match, so two `ensure_followers` tasks with different targets
 * silently shared one set.
 */
const getFollowTargets = (task: RedeemedSponsorTask): string[] =>
  Array.isArray(task.requirement?.followTargets)
    ? task.requirement.followTargets
    : [];

/** How many IG handles this task requires, or null when it declares none. */
const getFollowerCount = (task: RedeemedSponsorTask): number | null => {
  const value = task.requirement?.followerCount;
  return typeof value === "number" ? value : null;
};

/**
 * The cap on the required follower count, or null when the task has no formula.
 *
 * Used only to label a capped requirement honestly ("Up to 25 members") and to
 * bound the row-add affordance. The required number itself is always
 * `followerCount`; the client never computes it.
 */
const getFollowerCountCap = (task: RedeemedSponsorTask): number | null => {
  const value = task.requirement?.followerCountCap;
  return typeof value === "number" ? value : null;
};

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * Student sponsor task screen (port of the RN `SponsorTasksScreen`): shows the
 * official deadline / cure period, the compensation block once the failure
 * window closed, and the per-task proof submission (IG accounts, registered
 * usernames or media) with the matching AI review request.
 */
const StudentSponsorTasksPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sponsorActivityId = searchParams.get("sponsorActivityId") ?? "";

  const [sponsor, setSponsor] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<{ title: string; text: string } | null>(null);
  const [busyTaskId, setBusyTaskId] = useState<string | null>(null);
  const [polling, setPolling] = useState<Record<string, boolean>>({});
  const [optimisticStatus, setOptimisticStatus] = useState<Record<string, string>>({});
  const [polledStatus, setPolledStatus] = useState<Record<string, string>>({});
  const [uploadedProof, setUploadedProof] = useState<Record<string, File[]>>({});
  const [postUrlInputs, setPostUrlInputs] = useState<Record<string, string>>({});

  const [followersTask, setFollowersTask] = useState<RedeemedSponsorTask | null>(null);
  const [followersInput, setFollowersInput] = useState<string[]>([]);
  const [usernameTask, setUsernameTask] = useState<RedeemedSponsorTask | null>(null);
  const [usernameInput, setUsernameInput] = useState<string[]>([]);

  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [receiptUploading, setReceiptUploading] = useState(false);

  const [previewMedia, setPreviewMedia] = useState<ProofMedia | null>(null);
  const timers = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  const activity = sponsor?.activity ?? null;
  const tasks: RedeemedSponsorTask[] = Array.isArray(activity?.tasks) ? activity.tasks : [];
  const deadline: string | null = activity?.deadline ?? null;
  const committeeIGs: string[] = useMemo(() => {
    if (Array.isArray(activity?.committee_ig_accounts)) {
      return activity.committee_ig_accounts;
    }
    if (typeof activity?.committee_ig_accounts === "string") {
      try {
        const parsed = JSON.parse(activity.committee_ig_accounts);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }, [activity?.committee_ig_accounts]);

  const loadSponsor = useCallback(async () => {
    setLoading(true);
    const resp = await GetRedeemedSponsorsAPI(1, 200);
    setLoading(false);
    if (resp?.status !== 0) {
      setSponsor(null);
      return;
    }
    const found = (resp.result?.sponsors ?? []).find(
      (item) => item.activity?.id === sponsorActivityId,
    );
    setSponsor(found ?? null);
  }, [sponsorActivityId]);

  useEffect(() => {
    loadSponsor();
  }, [loadSponsor]);

  useEffect(
    () => () => {
      Object.values(timers.current).forEach((timer) => clearInterval(timer));
    },
    [],
  );

  const boundaries = useMemo(() => getDeadlineAndCureEnd(deadline), [deadline]);
  const hasPreDeadlineProof = useMemo(() => {
    if (!boundaries) return false;
    return getActivityHasPreDeadlineProof(tasks, boundaries.deadlineEnd);
  }, [tasks, boundaries]);

  const isNoCurePeriod = Boolean(
    boundaries && new Date() > boundaries.deadlineEnd && !hasPreDeadlineProof,
  );

  const isFailedAndCurePeriodOver = useMemo(() => {
    if (!deadline || !boundaries) return false;
    if (tasks.length > 0 && tasks.every((task) => isTaskCompleted(task))) return false;
    if (new Date() > boundaries.deadlineEnd && !hasPreDeadlineProof) return true;
    return new Date() > boundaries.cureEnd;
  }, [deadline, boundaries, tasks, hasPreDeadlineProof]);

  const showCompensation = isFailedAndCurePeriodOver || (isOnOrAfterEndOfCurePeriod(deadline) && anyTaskFailedMaxReview(tasks));

  const compensation = useMemo(() => {
    if (!boundaries) return calculateCompensation(tasks, null);
    const failedDate = isNoCurePeriod ? boundaries.deadlineEnd : boundaries.cureEnd;
    return calculateCompensation(tasks, failedDate);
  }, [tasks, boundaries, isNoCurePeriod]);

  const getEffectiveJobStatus = (task: RedeemedSponsorTask) =>
    resolveEffectiveJobStatus(
      task,
      optimisticStatus[task.id] || polledStatus[task.id],
    );

  const canReviewTask = (task: RedeemedSponsorTask) =>
    canAttemptActivity(tasks, deadline) && !isTaskFinallyFailed(tasks, task, deadline);

  const isTaskActionDisabled = (task: RedeemedSponsorTask) =>
    (task.review_count ?? 0) >= 3 ||
    busyTaskId === task.id ||
    Boolean(polling[task.id]) ||
    !canAttemptActivity(tasks, deadline) ||
    isNoCurePeriod ||
    isFailedAndCurePeriodOver ||
    !canReviewTask(task) ||
    (isOnOrAfterEndOfCurePeriod(deadline) && anyTaskFailedMaxReview(tasks));

  const curePeriodLocked = isOnOrAfterEndOfCurePeriod(deadline) && anyTaskFailedMaxReview(tasks);

  const startPollingJobStatus = (task: RedeemedSponsorTask, jobId: string) => {
    if (timers.current[task.id]) clearInterval(timers.current[task.id]);
    setPolling((prev) => ({ ...prev, [task.id]: true }));
    timers.current[task.id] = setInterval(async () => {
      try {
        const statusResp: any = await reviewEnsureFollowersStatusAPI({
          jobId,
          sponsorTaskCompletionId: task.sponsor_task_completion_id ?? undefined,
        });
        if (statusResp?.queue_job_status) {
          setPolledStatus((prev) => ({
            ...prev,
            [task.id]:
              statusResp.status === "waiting" ? "processing" : statusResp.queue_job_status,
          }));
        }
        if (statusResp?.status === 0) {
          clearInterval(timers.current[task.id]);
          delete timers.current[task.id];
          setPolling((prev) => ({ ...prev, [task.id]: false }));
          await loadSponsor();
        }
      } catch (err) {
        clearInterval(timers.current[task.id]);
        delete timers.current[task.id];
        setPolling((prev) => ({ ...prev, [task.id]: false }));
      }
    }, 5000);
  };

  const handleUploadReview = async (task: RedeemedSponsorTask) => {
    const files = uploadedProof[task.id] ?? [];
    if (files.length === 0) return;
    if (task.type === "publish_story_post" && files.length > 2) {
      setAlert({ title: t("Error"), text: t("You can upload a maximum of 2 images for this task.") });
      return;
    }
    let postUrl: string | undefined;
    if (task.type === "publish_story_post") {
      const candidate = (postUrlInputs[task.id] ?? task.proof?.post_url ?? "").trim();
      if (!isInstagramPostUrl(candidate)) {
        setAlert({
          title: t("Instagram Post URL Required"),
          text: t(
            "Enter the Instagram post URL that shows this redemption's proof code before uploading your screenshots.",
          ),
        });
        return;
      }
      postUrl = candidate;
    }
    setBusyTaskId(task.id);
    const urls = await cloudinaryUploadMedia(files, `uninfohk/sponsor/task_${task.id}`);
    const media: ProofMedia[] = files
      .map((file, index) => ({
        type: file.type.startsWith("video") ? "video" : "image",
        url: urls[index],
      }))
      .filter((item) => Boolean(item.url));
    setUploadedProof((prev) => ({ ...prev, [task.id]: [] }));
    try {
      const resp: any = await reviewUploadProofAPI({
        sponsorTaskCompletionId: task.sponsor_task_completion_id,
        media,
        ...(postUrl ? { post_url: postUrl } : {}),
      });
      const success = resp?.status === 0;
      const conflictsText =
        resp?.status === -40038
          ? describeEvidenceConflicts(resp.conflicts ?? [])
          : "";
      setAlert({
        title: success
          ? t("Success")
          : resp?.status === -40038
            ? t("Proof Already Used")
            : t("Error"),
        text:
          resp?.status === -40025
            ? t(
                "Your submission window has closed. You must submit at least one proof before the deadline to qualify for the cure period.",
              )
            : resp?.status === -40038
              ? conflictsText
                ? t(
                    "This proof was already used for another sponsor task: {{conflicts}}. Upload a different post or media.",
                    { conflicts: conflictsText },
                  )
                : t(
                    "This proof has already been used for another sponsor task.",
                  )
              : success
                ? t("Proof uploaded successfully!")
                : resp?.error || t("Failed to upload proof."),
      });
      if (success) await loadSponsor();
    } catch {
      setAlert({ title: t("Error"), text: t("Failed to upload proof.") });
    } finally {
      setBusyTaskId(null);
    }
  };

  const handleReviewTask = async (task: RedeemedSponsorTask) => {
    setOptimisticStatus((prev) => ({ ...prev, [task.id]: "processing" }));
    setBusyTaskId(task.id);
    try {
      if (task.type === "ensure_followers") {
        // Send exactly the accounts that were typed in. This used to pad the
        // list out to a count parsed out of the task title with empty strings,
        // which the backend could only score as "did not follow <target>", so a
        // short submission surfaced as the bare message "Failed with status 0".
        const proofList = followersInput
          .map((value) => value.trim().replace(/^@/, ""))
          .filter((value) => value !== "");
        // The required count and the accounts to follow both come from the
        // resolved `requirement` the API attached to THIS task. The count is
        // checked here purely to avoid a pointless round trip; the server
        // re-validates and is authoritative.
        const required = getFollowerCount(task);
        if (required !== null && proofList.length < required) {
          setAlert({
            title: t("Not Enough Accounts"),
            text: t(
              "Not enough IG accounts submitted: {{count}} of {{required}} required ({{missing}} missing).",
              {
                count: proofList.length,
                required,
                missing: required - proofList.length,
              },
            ),
          });
          setOptimisticStatus((prev) => ({ ...prev, [task.id]: "pending" }));
          return;
        }
        const resp: any = await reviewEnsureFollowersTaskAPI({
          followers: proofList,
          sponsorTaskCompletionId: task.sponsor_task_completion_id,
          // Sent for display/back-compat only; the server resolves the real
          // targets from `requirements.follow_targets`.
          targetIGAccounts: getFollowTargets(task),
        });
        if (resp?.status === 0 && resp.queue_job_id) {
          setAlert({
            title: t("Pending Review"),
            text: t("Review started. This may take a while. You can check status below."),
          });
          startPollingJobStatus(task, resp.queue_job_id);
        } else if (resp?.status === -40038) {
          const used = describeEvidenceConflicts(resp.conflicts ?? []);
          setOptimisticStatus((prev) => ({ ...prev, [task.id]: "pending" }));
          setAlert({
            title: t("Proof Already Used"),
            text: used
              ? t(
                  "These follower accounts were already used for another sponsor task: {{conflicts}}. Remove them and submit different accounts.",
                  { conflicts: used },
                )
              : t(
                  "This proof has already been used for another sponsor task.",
                ),
          });
        } else if (resp?.status === -40001) {
          const notFollowing: string[] = [];
          (resp.details ?? []).forEach((detail: any) => {
            (detail.results ?? []).forEach((result: any) => {
              if (!result.isFollower) notFollowing.push(`${result.follower} (${detail.target})`);
            });
          });
          setAlert({
            title: t("Validation Failed"),
            text: notFollowing.length > 0 ? notFollowing.join("; ") : t("Failed to review task."),
          });
          setOptimisticStatus((prev) => ({ ...prev, [task.id]: "pending" }));
        } else {
          setAlert({
            title: t("Error"),
            text: resp?.error || t("Failed to review task."),
          });
          setOptimisticStatus((prev) => ({ ...prev, [task.id]: "pending" }));
        }
        return;
      }

      if (task.type === "register_app") {
        const usernames = usernameInput.filter((value) => value.trim() !== "");
        const resp: any = await reviewRegisterAppTaskAPI({
          sponsorTaskCompletionId: task.sponsor_task_completion_id,
          registered_usernames: usernames,
        });
        const success = resp?.status === 0;
        setAlert({
          title: success ? t("Success") : t("Error"),
          text: success
            ? t("Usernames saved and review started!")
            : resp?.error || t("Failed to review register_app task."),
        });
        if (success) {
          setOptimisticStatus((prev) => ({ ...prev, [task.id]: "completed" }));
        } else {
          setOptimisticStatus((prev) => ({ ...prev, [task.id]: "pending" }));
        }
        return;
      }

      await handleUploadReview(task);
    } finally {
      setBusyTaskId(null);
    }
  };

  const handleSaveFollowers = async (task: RedeemedSponsorTask) => {
    const newAccounts = followersInput.filter((value) => value.trim() !== "");
    const existing = getIgAccountsProof(task.proof);
    if ([...newAccounts].sort().join(",") === [...existing].sort().join(",")) {
      setAlert({ title: t("Info"), text: t("No changes detected.") });
      return;
    }
    setBusyTaskId(task.id);
    try {
      const resp: any = await StoreSponsorTaskProofAPI({
        id: task.sponsor_task_completion_id ?? "",
        proof: { ig_accounts: newAccounts },
      });
      const success = resp?.status === 0;
      setAlert({
        title: success ? t("Success") : t("Error"),
        text:
          resp?.status === -40025
            ? t(
                "Your submission window has closed. You must submit at least one proof before the deadline to qualify for the cure period.",
              )
            : success
              ? t("Proof submitted successfully!")
              : resp?.error || t("Failed to submit proof."),
      });
      if (success) await loadSponsor();
    } catch {
      setAlert({ title: t("Error"), text: t("Failed to submit proof.") });
    } finally {
      setBusyTaskId(null);
      setFollowersTask(null);
    }
  };

  const handleSaveUsernames = async (task: RedeemedSponsorTask) => {
    const newUsernames = usernameInput.filter((value) => value.trim() !== "");
    const existing = getRegisteredUsernamesProof(task.proof);
    if ([...newUsernames].sort().join(",") === [...existing].sort().join(",")) {
      setAlert({ title: t("Info"), text: t("No changes detected.") });
      return;
    }
    setBusyTaskId(task.id);
    try {
      const resp: any = await StoreRegisteredUsernamesProofAPI({
        id: task.sponsor_task_completion_id ?? "",
        proof: { registered_usernames: newUsernames },
      });
      const success = resp?.status === 0;
      setAlert({
        title: success ? t("Success") : t("Error"),
        text: success
          ? t("Usernames saved successfully!")
          : resp?.error || t("Failed to save usernames."),
      });
      if (success) await loadSponsor();
    } catch {
      setAlert({ title: t("Error"), text: t("Failed to save registered usernames.") });
    } finally {
      setBusyTaskId(null);
      setUsernameTask(null);
    }
  };

  const handleUploadReceipt = async () => {
    if (!receiptFile) return;
    setReceiptUploading(true);
    try {
      const receiptUrl = await readFileAsDataUrl(receiptFile);
      const cdnUrl = await cloudinaryUpload(receiptUrl);
      if (!cdnUrl) {
        setAlert({
          title: t("Upload Failed"),
          text: t("Failed to upload receipt image to CDN. Please try again."),
        });
        return;
      }
      const resp: any = await compensationUploadAPI({
        sponsorActivityId,
        compensation_receipt_url: cdnUrl,
        compensation_amount: compensation,
      });
      const success = resp?.status === 0;
      setAlert({
        title: success ? t("Success") : t("Error"),
        text: success
          ? t("Compensation receipt uploaded!")
          : resp?.error || t("Failed to upload compensation receipt. Please try again."),
      });
      if (success) {
        setReceiptFile(null);
        await loadSponsor();
      }
    } catch {
      setAlert({
        title: t("Error"),
        text: t("Failed to upload compensation receipt. Please try again."),
      });
    } finally {
      setReceiptUploading(false);
    }
  };

  const handleChat = async (type: "sponsor" | "admin") => {
    if (type === "admin") {
      const room = await openChatRoom({ type: "admin" });
      if (room?.id) navigate(`/student/chat/${room.id}`);
      return;
    }
    if (!sponsor?.user_id) return;
    const room = await openChatRoom({
      receiver_user_id: sponsor.user_id,
      type: "sponsor",
      sponsor_activity_id: sponsorActivityId,
    });
    if (room?.id) navigate(`/student/chat/${room.id}`);
  };

  const openFollowersModal = (task: RedeemedSponsorTask) => {
    const existing = getIgAccountsProof(task.proof);
    // Start from what was already typed or saved. Only when there is nothing at
    // all do we seed a single blank row for the input to bind to — this used to
    // pre-render `accountCount` empty rows, a number parsed out of the task
    // title, which fed the padding that produced "Failed with status 0".
    const source = followersInput.some((value) => value.trim() !== "")
      ? followersInput
      : existing.length > 0
        ? existing
        : [""];
    setFollowersInput(
      source.map((value) => {
        const trimmed = String(value ?? "").trim();
        return trimmed !== "" && !trimmed.startsWith("@") ? `@${trimmed}` : trimmed;
      }),
    );
    setFollowersTask(task);
  };

  const openUsernamesModal = (task: RedeemedSponsorTask) => {
    const existing = getRegisteredUsernamesProof(task.proof);
    const source =
      usernameInput.some((value) => value.trim() !== "")
        ? usernameInput
        : existing.length > 0
          ? existing
          : Array(committeeIGs.length).fill("");
    setUsernameInput(source.slice(0, Math.max(committeeIGs.length, 1)));
    setUsernameTask(task);
  };

  const openStatusModal = (text: string) => setAlert({ title: t("Review Failure Reason"), text });

  const renderTaskActions = (task: RedeemedSponsorTask) => {
    if (curePeriodLocked) return null;
    if (!ACTIONABLE_TASK_TYPES.includes(task.type ?? "")) return null;

    const effectiveJobStatus = getEffectiveJobStatus(task);
    if (effectiveJobStatus === "completed") return null;
    if (effectiveJobStatus === "processing") {
      return (
        <p className="mt-3 text-right text-[13px] font-bold text-[#ffa217]">
          {task.queue_job_id || polledStatus[task.id]
            ? t("Review in progress by AI...")
            : t("Awaiting review")}
        </p>
      );
    }

    const disabled = isTaskActionDisabled(task);

    if (task.type === "ensure_followers") {
      const showReviewButton =
        ((uploadedProof[task.id]?.length ?? 0) > 0 || followersInput.length > 0) &&
        !polledStatus[task.id];
      return (
        <div className="mt-3 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => openFollowersModal(task)}
            disabled={disabled}
            className="rounded-lg bg-[#ffa217] px-3 py-1.5 text-[13px] font-bold text-white disabled:opacity-50"
          >
            {t("Input IG Accounts")}
          </button>
          {showReviewButton && (
            <button
              type="button"
              onClick={() => handleReviewTask(task)}
              disabled={disabled}
              className="rounded-lg bg-[#ffa217] px-3 py-1.5 text-[13px] font-bold text-white disabled:opacity-50"
            >
              {t("Review Task")}
            </button>
          )}
        </div>
      );
    }

    if (["follow_and_like_post", "register_app", "publish_story_post"].includes(task.type ?? "")) {
      if (task.completed) return null;
      const hasInput =
        (uploadedProof[task.id]?.length ?? 0) > 0 || usernameInput.length > 0;
      const showReviewButton = hasInput && !polledStatus[task.id];
      if (task.type === "register_app") {
        return (
          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => openUsernamesModal(task)}
              disabled={disabled}
              className="rounded-lg bg-[#ffa217] px-3 py-1.5 text-[13px] font-bold text-white disabled:opacity-50"
            >
              {getRegisteredUsernamesProof(task.proof)[0]
                ? t("Username: {{value}}", { value: getRegisteredUsernamesProof(task.proof)[0] })
                : t("Input Username")}
            </button>
            {showReviewButton && (
              <button
                type="button"
                onClick={() => handleReviewTask(task)}
                disabled={disabled}
                className="rounded-lg bg-[#ffa217] px-3 py-1.5 text-[13px] font-bold text-white disabled:opacity-50"
              >
                {t("Review Task")}
              </button>
            )}
          </div>
        );
      }
      return (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          <label
            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-[#ffa217] px-3 py-1.5 text-[13px] font-bold text-white ${
              disabled ? "pointer-events-none opacity-50" : ""
            }`}
          >
            <Upload size={14} />
            {task.type === "follow_and_like_post"
              ? t("Upload Video")
              : task.type === "publish_story_post"
                ? t("Upload 2 Images")
                : t("Upload Image/Video")}
            <input
              type="file"
              className="hidden"
              accept={task.type === "follow_and_like_post" ? "video/*" : undefined}
              multiple={task.type !== "publish_story_post"}
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                setUploadedProof((prev) => ({ ...prev, [task.id]: files }));
                e.target.value = "";
              }}
            />
          </label>
          {(uploadedProof[task.id]?.length ?? 0) > 0 && (
            <span className="text-xs text-gray-500">
              {t("{{count}} file(s) selected", { count: uploadedProof[task.id].length })}
            </span>
          )}
          {showReviewButton && (
            <button
              type="button"
              onClick={() => handleReviewTask(task)}
              disabled={disabled}
              className="rounded-lg bg-[#ffa217] px-3 py-1.5 text-[13px] font-bold text-white disabled:opacity-50"
            >
              {busyTaskId === task.id ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                t("Review Task")
              )}
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        <label
          className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-[#ffa217] px-3 py-1.5 text-[13px] font-bold text-white ${
            disabled ? "pointer-events-none opacity-50" : ""
          }`}
        >
          <Upload size={14} />
          {t("Upload Image/Video")}
          <input
            type="file"
            className="hidden"
            multiple
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              setUploadedProof((prev) => ({ ...prev, [task.id]: files }));
              e.target.value = "";
            }}
          />
        </label>
        {(uploadedProof[task.id]?.length ?? 0) > 0 && (
          <button
            type="button"
            onClick={() => handleReviewTask(task)}
            disabled={disabled}
            className="rounded-lg bg-[#ffa217] px-3 py-1.5 text-[13px] font-bold text-white disabled:opacity-50"
          >
            {t("Review Task")}
          </button>
        )}
      </div>
    );
  };

  const deadlineDate = deadline ? parseLocalDate(deadline) : null;
  const cureEnd = deadline ? getLastCureDay(deadline) : null;

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-3 inline-flex items-center gap-1.5 text-sm font-bold text-gray-500 hover:text-[#ffa217]"
      >
        <ChevronDown size={16} className="rotate-90" />
        {t("Back")}
      </button>

      <h1 className="text-2xl font-bold text-gray-900">{t("Sponsor Tasks")}</h1>

      {loading ? (
        <div className="mt-6 flex justify-center">
          <Loader2 size={22} className="animate-spin text-[#ffa217]" />
        </div>
      ) : !sponsor || !activity ? (
        <div className="mt-4 rounded-2xl border border-gray-100 bg-white">
          <AdminEmptyState
            iconName="alert-circle-outline"
            title={t("Sponsor activity not found")}
            message={t("Please go back and try again.")}
            actionLabel={t("Retry")}
            onAction={loadSponsor}
          />
        </div>
      ) : (
        <>
          {deadline && (
            <div className="mt-4 rounded-xl border border-white bg-[#e6e5e5] p-3">
              <p className="text-[15px] leading-[22px] font-bold text-[#ff0404]">
                {t("Official Deadline for All Tasks: {{date}}", {
                  date: deadlineDate ? formatMMDDYYYY(deadlineDate) : deadline,
                })}
              </p>
              <p className="mt-1 flex gap-2 text-[13px] leading-[19px] text-black">
                <Info size={16} className="mt-0.5 shrink-0 text-[#ffa217]" />
                {t(
                  "You can upload proof or review your task from the activity start until the deadline. If you submit at least one proof before the deadline, you will have an extra 7-day cure period after the deadline to upload or review.",
                )}
              </p>
              {deadlineDate && (
                <div className="mt-1 space-y-1 text-[13px] leading-[19px] font-bold text-black">
                  <p className="flex gap-2">
                    <Info size={16} className="mt-0.5 shrink-0 text-[#ff9800]" />
                    {t(
                      "Note: If you did not upload any proof before or on the deadline ({{date}}), there is NO cure period after the deadline.",
                      { date: formatMMDDYYYY(deadlineDate) },
                    )}
                  </p>
                  {!hasPreDeadlineProof && (
                    <>
                      <p className="flex gap-2">
                        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-[#ff0404]" />
                        {t(
                          "You must submit at least one proof before the deadline to be eligible for the 7-day cure period after the deadline.",
                        )}
                      </p>
                      {isNoCurePeriod && (
                        <p className="flex gap-2">
                          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-[#ff0404]" />
                          {t(
                            "According to the agreement, you have failed the task and are required to pay compensation.",
                          )}
                        </p>
                      )}
                    </>
                  )}
                  {hasPreDeadlineProof &&
                    new Date() > deadlineDate &&
                    cureEnd && (
                      <p>
                        {t(
                          "Cure Period: {{start}} to {{end}} (7 days after deadline) You may upload or review your task only if you submitted proof before the deadline.",
                          { start: formatMMDDYYYY(deadlineDate), end: formatMMDDYYYY(cureEnd) },
                        )}
                      </p>
                    )}
                </div>
              )}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleChat("sponsor")}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#ffa217] py-2.5 text-sm font-bold text-white"
            >
              <MessageCircle size={18} />
              {t("Chat with Business")}
            </button>
            <button
              type="button"
              onClick={() => handleChat("admin")}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#ffa217] py-2.5 text-sm font-bold text-white"
            >
              <MessageCircle size={18} />
              {t("Chat with Admin")}
            </button>
          </div>

          {showCompensation && (
            <div className="mt-3 rounded-xl border border-red-500 bg-[#fff3e0] px-5 py-3">
              {activity.compensation_receipt_url ? (
                <div className="mb-3 flex flex-col items-center">
                  <p className="mb-2 text-base font-bold text-[#ffa217]">
                    {t("Compensation Receipt")}
                  </p>
                  <img
                    src={activity.compensation_receipt_url}
                    alt={t("Compensation Receipt")}
                    className="mb-2 max-h-64 max-w-full rounded-lg object-contain"
                  />
                  {activity.compensation_approval_status === "approved" && (
                    <p className="text-[15px] font-bold text-[#4CAF50]">
                      {t("Your compensation receipt has been approved.")}
                    </p>
                  )}
                  {activity.compensation_approval_status === "rejected" && (
                    <>
                      <p className="text-[15px] font-bold text-black">
                        {t("Your compensation receipt was rejected. Please upload a new valid receipt.")}
                      </p>
                      {activity.compensation_reject_reason && (
                        <p className="mt-1 text-sm text-black">
                          {t("Reason: {{reason}}", { reason: activity.compensation_reject_reason })}
                        </p>
                      )}
                    </>
                  )}
                  {activity.compensation_approval_status === "pending" && (
                    <p className="text-[15px] font-bold text-[#ffa217]">
                      {t("Your compensation receipt is under review.")}
                    </p>
                  )}
                </div>
              ) : null}

              {(!activity.compensation_receipt_url ||
                activity.compensation_approval_status === "rejected") && (
                <>
                  <p className="mb-2 text-lg font-bold text-[#ff0000]">
                    {t("Compensation due: HKD ${{amount}}", {
                      amount: compensation.toLocaleString(),
                    })}
                  </p>
                  <label className="flex cursor-pointer items-center justify-between gap-2 text-[15px] font-bold text-[#ff0000]">
                    <span className="whitespace-pre-line">
                      {t(
                        "You must upload a compensation receipt. The receipt must clearly show: - The name of the recipient - The date of payment - The name of the person paying the compensation",
                      )}
                    </span>
                    <Upload size={26} className="shrink-0" />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        setReceiptFile(file);
                        setReceiptPreview(file ? URL.createObjectURL(file) : null);
                      }}
                    />
                  </label>
                </>
              )}

              {receiptFile && (
                <div className="mt-3 flex flex-col items-center gap-2">
                  {receiptPreview && (
                    <img
                      src={receiptPreview}
                      alt={t("Compensation Receipt")}
                      className="max-h-64 max-w-full rounded-lg object-contain"
                    />
                  )}
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleUploadReceipt}
                      disabled={receiptUploading}
                      className="rounded-lg bg-[#ffa217] px-5 py-2 text-sm font-bold text-white disabled:opacity-60"
                    >
                      {receiptUploading ? t("Uploading...") : t("Upload")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setReceiptFile(null);
                        setReceiptPreview(null);
                      }}
                      className="rounded-lg px-5 py-2 text-sm font-bold text-gray-700"
                    >
                      {t("Cancel")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activity.proof_code &&
            tasks.some((task) => task.type === "publish_story_post") && (
              <div className="mt-3 rounded-xl border border-[#ffa217] bg-[#fff7e6] p-4 text-center">
                <p className="text-sm font-bold text-gray-800">
                  {t("Your Proof Code")}
                </p>
                <p className="my-1 text-3xl font-black tracking-[0.2em] text-[#ffa217]">
                  {activity.proof_code}
                </p>
                <p className="text-xs font-semibold text-red-600">
                  {t(
                    "The proof code must be clearly visible in your Instagram Story/Post. Do not reuse the same Story/Post for another sponsor.",
                  )}
                </p>
              </div>
            )}

          <div className="mt-4 space-y-3">
            {tasks.length > 0 ? (
              tasks.map((task, index) => {
                const effectiveStatus = getEffectiveJobStatus(task);
                const isFailed = effectiveStatus === "failed";
                const isCompleted = isTaskCompleted(task);
                const isReady = effectiveStatus === "ready";
                const isFinallyFailed = isTaskFinallyFailed(tasks, task, deadline);
                const reviewCount = task.review_count ?? 0;

                let failReason: string | undefined =
                  task.proof && typeof task.proof === "object"
                    ? task.proof.fail_reason
                    : undefined;
                if (
                  isFailedAndCurePeriodOver &&
                  (!task.proof || (Array.isArray(task.proof) && task.proof.length === 0))
                ) {
                  failReason = "Did not upload any proof for this task.";
                }

                const proofMedia = getProofMedia(task.proof);
                const uploadedFiles = uploadedProof[task.id] ?? [];

                return (
                  <div
                    key={task.id}
                    className={`rounded-2xl border bg-white p-4 shadow-sm ${
                      isCompleted ? "border-[#4CAF50]" : "border-gray-100"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        <p className="text-base font-bold text-gray-900">
                          {t("Task {{index}}", { index: index + 1 })}
                        </p>
                        {(isFinallyFailed || (isFailed && failReason)) && !isCompleted && (
                          <button
                            type="button"
                            onClick={() =>
                              openStatusModal(
                                failReason ||
                                  t(
                                    "You have failed this task due to missing proof after the cure period.",
                                  ),
                              )
                            }
                            aria-label={t("Show fail reason")}
                          >
                            <ShieldAlert
                              size={20}
                              className={isFinallyFailed ? "text-red-500" : "text-[#ff9800]"}
                            />
                          </button>
                        )}
                      </div>
                      <p
                        className={`text-sm font-bold ${
                          isCompleted
                            ? "text-[#4CAF50]"
                            : isFinallyFailed
                              ? "text-red-500"
                              : isReady || isFailed
                                ? "text-[#ff9800]"
                                : "text-gray-500"
                        }`}
                      >
                        {isCompleted
                          ? `✅ ${t("Completed")}`
                          : isFinallyFailed
                            ? `❌ ${t("Failed")}`
                            : isReady
                              ? t("Awaiting check")
                              : isFailed
                                ? `⏳ ${t("Awaiting re-submit")}`
                                : `⏳ ${t("Incomplete")}`}
                      </p>
                    </div>

                    <p className="mt-2 text-base font-normal text-gray-900">
                      {task.title || ""}
                    </p>
                    {task.description && (
                      <p className="mt-1 text-sm text-gray-500">{task.description}</p>
                    )}

                    {task.type === "publish_story_post" && (
                      <label className="mt-2 block">
                        <span className="mb-1 block text-xs font-bold text-gray-700">
                          {t("Instagram Post URL")}
                        </span>
                        <input
                          type="url"
                          value={
                            postUrlInputs[task.id] ??
                            task.proof?.post_url ??
                            ""
                          }
                          onChange={(event) =>
                            setPostUrlInputs((prev) => ({
                              ...prev,
                              [task.id]: event.target.value,
                            }))
                          }
                          placeholder="https://www.instagram.com/p/..."
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#ffa217]"
                        />
                      </label>
                    )}

                    {proofMedia.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {proofMedia.map((media, mediaIndex) => (
                          <button
                            key={`${media.url}-${mediaIndex}`}
                            type="button"
                            onClick={() => setPreviewMedia(media)}
                            className="h-16 w-16 overflow-hidden rounded-lg bg-gray-100"
                          >
                            {media.type === "image" ? (
                              <img
                                src={media.url}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <span className="flex h-full w-full items-center justify-center text-2xl">
                                🎬
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}

                    {uploadedFiles.length > 0 && (
                      <p className="mt-2 inline-flex items-center gap-1 rounded-lg bg-gray-50 px-2 py-1 text-xs text-gray-600">
                        <Paperclip size={12} />
                        {t("{{count}} file(s) ready to submit", { count: uploadedFiles.length })}
                      </p>
                    )}

                    {typeof task.review_count === "number" && task.review_count > 0 && !isCompleted && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex gap-1">
                          {[0, 1, 2].map((dot) => {
                            const used = dot < reviewCount;
                            const isLastUsed = used && dot === reviewCount - 1;
                            let color = "#e0e0e0";
                            if (isCompleted) {
                              color = used && isLastUsed ? "#4caf50" : used ? "#f44336" : "#e0e0e0";
                            } else if (used) {
                              color = "#f44336";
                            }
                            return (
                              <span
                                key={dot}
                                className="h-2.5 w-2.5 rounded-full"
                                style={{ backgroundColor: color }}
                              />
                            );
                          })}
                        </div>
                        <span
                          className={`text-[11px] font-semibold ${
                            reviewCount >= 3 ? "text-[#f44336]" : "text-gray-600"
                          }`}
                        >
                          {`${reviewCount}/3`}
                        </span>
                        {isFailed && reviewCount < 3 && (
                          <span className="text-[11px] text-[#ff9800]">
                            {`· ${t("Awaiting re-submit")}`}
                          </span>
                        )}
                      </div>
                    )}

                    {isFailed && typeof task.review_count === "number" && (
                      <div className="mt-2 text-right">
                        <p className="text-[13px] font-bold text-gray-700">
                          {t("Review attempts: {{count}} / 3", { count: task.review_count })}
                        </p>
                        {task.review_count >= 3 && !task.completed && (
                          <p className="text-[13px] font-bold text-red-500">
                            {t(
                              "You have failed 3 times. There is no extra opportunity for you to complete this task.",
                            )}
                          </p>
                        )}
                      </div>
                    )}

                    {renderTaskActions(task)}
                  </div>
                );
              })
            ) : (
              <div className="rounded-2xl border border-gray-100 bg-white">
                <AdminEmptyState
                  iconName="checkbox-outline"
                  title={t("No tasks found.")}
                  message={t("This sponsor has no tasks yet.")}
                />
              </div>
            )}
          </div>
        </>
      )}

      <Modal visible={Boolean(alert)} onClose={() => setAlert(null)}>
        <div className="rounded-2xl bg-white p-5 text-center shadow-lg">
          {alert?.title && <p className="mb-2 text-base font-bold text-gray-900">{alert.title}</p>}
          <p className="mb-4 text-sm text-gray-700">{alert?.text}</p>
          <button
            type="button"
            onClick={() => setAlert(null)}
            className="w-full rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>

      <Modal visible={Boolean(previewMedia)} onClose={() => setPreviewMedia(null)} fullScreen>
        <div className="relative flex h-full w-full items-center justify-center bg-black/90">
          {previewMedia?.type === "image" ? (
            <img
              src={previewMedia.url}
              alt=""
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <video src={previewMedia?.url} controls className="max-h-full max-w-full" />
          )}
          <button
            type="button"
            onClick={() => setPreviewMedia(null)}
            className="absolute right-4 top-4 rounded-full bg-white/90 p-2 text-gray-900"
            aria-label={t("Close")}
          >
            <X size={20} />
          </button>
        </div>
      </Modal>

      <Modal
        visible={Boolean(followersTask)}
        onClose={() => setFollowersTask(null)}
      >
        <div className="max-h-[80vh] overflow-y-auto rounded-2xl bg-white p-5 shadow-lg">
          <h3 className="mb-1 text-base font-bold text-gray-900">
            {t("Input IG Accounts")}
          </h3>
          <p className="mb-3 text-xs text-gray-500">
            {t("Enter the IG usernames of the students who followed the accounts.")}
          </p>
          {(() => {
            if (!followersTask) return null;
            const required = getFollowerCount(followersTask);
            if (required === null) return null;
            const cap = getFollowerCountCap(followersTask);
            const capped = cap !== null && required >= cap;
            return (
              <p className="mb-3 text-xs font-bold text-[#ffa217]">
                {capped
                  ? t("Up to {{cap}} members must follow the accounts.", { cap })
                  : t("Enter at least {{count}} accounts.", { count: required })}
              </p>
            );
          })()}
          <div className="space-y-2">
            {followersInput.map((value, index) => (
              <input
                key={index}
                type="text"
                value={value}
                onChange={(e) =>
                  setFollowersInput((prev) =>
                    prev.map((item, itemIndex) =>
                      itemIndex === index ? e.target.value : item,
                    ),
                  )
                }
                placeholder="@username"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#ffa217]"
              />
            ))}
            {followersInput.length <
              (followersTask ? getFollowerCountCap(followersTask) ?? 200 : 200) && (
              <button
                type="button"
                onClick={() => setFollowersInput((prev) => [...prev, ""])}
                className="w-full rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm font-bold text-gray-500 hover:border-[#ffa217] hover:text-[#ffa217]"
              >
                {t("Add another account")}
              </button>
            )}
          </div>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => followersTask && handleSaveFollowers(followersTask)}
              className="flex-1 rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white"
            >
              {t("Submit")}
            </button>
            <button
              type="button"
              onClick={() => setFollowersTask(null)}
              className="flex-1 rounded-xl bg-gray-100 py-2.5 text-sm font-bold text-gray-600"
            >
              {t("Cancel")}
            </button>
          </div>
        </div>
      </Modal>

      <Modal visible={Boolean(usernameTask)} onClose={() => setUsernameTask(null)}>
        <div className="max-h-[80vh] overflow-y-auto rounded-2xl bg-white p-5 shadow-lg">
          <h3 className="mb-1 text-base font-bold text-gray-900">{t("Input Username")}</h3>
          <p className="mb-3 text-xs text-gray-500">
            {t("Enter the registered username of each committee member.")}
          </p>
          <div className="space-y-2">
            {usernameInput.map((value, index) => (
              <input
                key={index}
                type="text"
                value={value}
                onChange={(e) =>
                  setUsernameInput((prev) =>
                    prev.map((item, itemIndex) =>
                      itemIndex === index ? e.target.value : item,
                    ),
                  )
                }
                placeholder="@username"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#ffa217]"
              />
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => usernameTask && handleSaveUsernames(usernameTask)}
              className="flex-1 rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white"
            >
              {t("Submit")}
            </button>
            <button
              type="button"
              onClick={() => setUsernameTask(null)}
              className="flex-1 rounded-xl bg-gray-100 py-2.5 text-sm font-bold text-gray-600"
            >
              {t("Cancel")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StudentSponsorTasksPage;
